import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { API_ENDPOINTS } from '../config/apiRouting';
import { BLOG_POSTS, slugify } from '../data/blogContent';
import './EnhancedMarketingLandingPage.css';
import './BlogPage.css';

import logoBlack from '../assets/images/logo-black.png';

interface ContactFormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}

const EnhancedMarketingLandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showGetStartedModal, setShowGetStartedModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowDemoModal(false);
        setShowGetStartedModal(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (showDemoModal || showGetStartedModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDemoModal, showGetStartedModal]);

  const validateForm = (): boolean => {
    const errors: Partial<ContactFormData> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    }

    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      message: ''
    });
    setFormErrors({});
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent, formType: 'getStarted' | 'demo') => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(API_ENDPOINTS.contactForm(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          formType,
          subject: formType === 'demo' ? 'Demo Request' : 'Sales Inquiry',
          to: 'chris@bigsystemone.com'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        if (formType === 'demo') {
          setShowDemoModal(false);
        } else {
          setShowGetStartedModal(false);
        }
        resetForm();
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const pillars = [
    { id: 'pos', title: 'Point of Sale', blurb: 'Android POS that keeps taking orders when the internet drops, with guest checks, kitchen send, and integrated payments.' },
    { id: 'menus', title: 'Menus and Pricing', blurb: 'One menu source of truth pushed to every store, board, QR code, and POS, with pricing by enterprise, concept, or location.' },
    { id: 'front-of-house', title: 'Front of House', blurb: 'Waitlist with SMS, a floor-plan editor synced to the POS, kitchen display, and staff alerts that reach every screen.' },
    { id: 'inventory', title: 'Inventory and Kitchen Intelligence', blurb: 'Recipes, receiving, suppliers, AI demand forecasts, generated purchase orders, invoice scanning, and waste analytics.' },
    { id: 'reputation', title: 'Guest Reputation and Marketing', blurb: 'Reviews, guest recovery, social posting, offer codes, and micro-surveys that close the loop with unhappy guests.' },
    { id: 'voice', title: 'Voice and Phone AI', blurb: 'An AI phone assistant that answers every call, and voice ordering at the counter with upsell rules you control.' },
    { id: 'people', title: 'People and Scheduling', blurb: 'Employees, schedules, jobs, labor rules, conflict and overtime alerts, and a native employee app.' },
    { id: 'analytics', title: 'Analytics and Command Center', blurb: 'Revenue, menu, team, and recovery performance in one place, plus plain-English questions answered from your data.' },
    { id: 'platform', title: 'Enterprise Platform and API', blurb: 'Multi-unit hierarchy, equipment and network monitoring, security auditing, and a public API with webhooks.' },
  ];

  const integrations = [
    'Square', 'Clover', 'Priv8', 'Google Business Profile', 'Yelp', 'Facebook', 'Twilio', 'Stripe', 'AccuWeather', 'Ubiquiti UniFi'
  ];

  const screenshots = [
    { src: '/MainMenuPOS.jpg', label: 'POS Order Entry' },
    { src: '/CustomizeOrder.jpg', label: 'Item Customization' },
    { src: '/KitchenDisplay.jpg', label: 'Kitchen Display' },
    { src: '/screenshots/Screenshot-2026-03-09-at-12.00.03-PM.png', label: 'Floor Plan Editor' },
    { src: '/screenshots/TableManagement.png', label: 'Table Management' },
    { src: '/MenuManagement.png', label: 'Menu Management' },
    { src: '/OnlineMenuManagement.png', label: 'Online Menu' },
    { src: '/VoiceOrderingSim.png', label: 'Voice Ordering Simulator' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.49.35-AM.png', label: 'Review Management' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.49.59-AM.png', label: 'Review Inbox' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.50.49-AM.png', label: 'Guest Recovery Inbox' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.51.11-AM.png', label: 'Recovery Response Editor' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.58.51-AM.png', label: 'Recovery Analytics' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.49.13-AM.png', label: 'Social Media Dashboard' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.51.43-AM.png', label: 'Scheduled Posts' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.51.59-AM.png', label: 'Social Calendar' },
    { src: '/ScheduleApp.png', label: 'Employee Scheduling' },
    { src: '/RestaurantMessaging.png', label: 'Restaurant Messaging' },
    { src: '/WeatherDataManagement.png', label: 'Weather Analytics' },
    { src: '/MultiUnitManagement.png', label: 'Multi-Unit Management' },
    { src: '/screenshots/Screenshot-2026-03-09-at-11.52.24-AM.png', label: 'Store Onboarding' },
    { src: '/EmployeeApp.png', label: 'Employee App (iOS)' },
  ];

  const faqItems = [
    {
      question: 'Is BigSystemOne a POS, or does it work with my existing POS?',
      answer: 'Both. BigSystemOne includes its own Android point of sale, and it also connects to Square and Clover so you can keep your current POS and still use menus, reputation, voice, inventory, and analytics on top of it.'
    },
    {
      question: 'Can I manage multiple locations, concepts, or franchisees?',
      answer: 'Yes. The platform is built around an enterprise hierarchy: enterprise, concept, franchisee, organizational unit, and store. Menus, pricing, permissions, and reports can be set at any level and inherited down.'
    },
    {
      question: 'Which review and social platforms are supported?',
      answer: 'Reviews are monitored and answered for Google and Yelp, with AI-drafted responses you approve. Social posting and scheduling works with Facebook and Google Business Profile, with additional networks configurable per account.'
    },
    {
      question: 'How does the phone assistant work?',
      answer: 'Calls to your restaurant number are answered by a realtime voice AI that knows your hours, menu, and policies. It handles common questions and takes orders or reservations based on how you configure it, and every call is logged with a transcript and intent so you can review what guests asked.'
    },
    {
      question: 'Does the POS work when the internet goes down?',
      answer: 'Yes. The Android POS stores orders and checks locally and syncs automatically when connectivity returns, so service continues during an outage.'
    },
    {
      question: 'Can I import my existing menu?',
      answer: 'Yes. Import menus by CSV, extract them from a PDF, or enter items manually. Modifier templates and AI-generated menu photos speed up the rest.'
    },
    {
      question: 'Is there an API?',
      answer: 'Yes. A public REST API with sandbox and production keys, scoped permissions, rate limiting, and signed webhooks for around forty domain events. Documentation and key management live in the developer portal.'
    },
    {
      question: 'How do I get started?',
      answer: 'Request a demo and we will walk through your locations, current POS, and which modules matter most to you. Onboarding covers your restaurant profile, POS or ordering setup, loyalty, social accounts, and reviews.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>BigSystemOne - The Restaurant Operating System | POS, Menus, Inventory, Voice AI, Reputation, Scheduling</title>
        <meta name="description" content="BigSystemOne runs the whole restaurant from one platform: offline-first POS, menus and pricing, waitlist and floor plans, inventory intelligence, guest reputation and marketing, voice and phone AI, scheduling, analytics, and a public API. Built for single locations and multi-unit enterprises." />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="keywords" content="restaurant operating system, restaurant POS, offline POS, restaurant inventory management, AI demand forecasting, restaurant phone assistant, voice ordering, review management, guest recovery, social media for restaurants, waitlist management, table management, employee scheduling, multi-unit restaurant management, restaurant API" />
      </Helmet>

      <div className="landing">
        {/* Header */}
        <header className={`header ${scrolled ? 'header-scrolled' : ''}`}>
          <div className="container">
            <div className="nav-brand">
              <img src={logoBlack} alt="BigSystemOne" className="logo" />
            </div>

            <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
              <a href="#platform" onClick={(e) => { e.preventDefault(); scrollToSection('platform'); }}>Platform</a>
              <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>Features</a>
              <a href="#apps" onClick={(e) => { e.preventDefault(); scrollToSection('apps'); }}>Apps</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>Pricing</a>
              <Link to="/developers" onClick={() => setIsMenuOpen(false)}>Developers</Link>
              <Link to="/blog" onClick={() => setIsMenuOpen(false)}>Blog</Link>
              <Link to="/login" className="nav-cta" onClick={() => setIsMenuOpen(false)}>
                Access Platform
              </Link>
            </nav>

            <button
              className="menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <section className="hero">
          <div className="container">
            <div className="hero-content hero-content-centered">
              <div className="hero-text hero-text-centered">
                <div className="badge">The Restaurant Operating System</div>
                <h1 className="hero-title">
                  Run the Whole Restaurant
                  <span className="gradient-text"> From One Platform</span>
                </h1>
                <p className="hero-description">
                  Point of sale, menus and pricing, front of house, inventory intelligence, guest reputation,
                  voice and phone AI, scheduling, and analytics. Built for single locations and multi-unit enterprises,
                  with native apps for the counter and the team.
                </p>
                <div className="hero-actions hero-actions-centered">
                  <button className="btn-primary" onClick={() => setShowDemoModal(true)}>Request a Demo</button>
                  <button className="btn-secondary" onClick={() => scrollToSection('platform')}>Explore the Platform</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations Bar */}
        <section className="trust-bar">
          <div className="container">
            <p className="trust-bar-label">Works with the systems you already run</p>
            <div className="trust-bar-items">
              {integrations.map((name) => (
                <div className="trust-bar-item" key={name}>{name}</div>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Overview */}
        <section id="platform" className="lp-platform">
          <div className="container">
            <div className="section-header">
              <h2>Everything a Restaurant Runs On</h2>
              <p>Nine connected modules on one data model. Turn on what you need; everything shares the same menus, locations, people, and guests.</p>
            </div>
            <div className="lp-platform-grid">
              {pillars.map((p) => (
                <a
                  key={p.id}
                  href={`#${p.id}`}
                  className="lp-platform-card"
                  onClick={(e) => { e.preventDefault(); scrollToSection(p.id); }}
                >
                  <h3>{p.title}</h3>
                  <p>{p.blurb}</p>
                  <span className="lp-platform-card-link">See details &rarr;</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Sections */}
        <div id="features">
          {/* Point of Sale */}
          <section id="pos" className="feature-section">
            <div className="container">
              <div className="feature-grid">
                <div className="feature-image-placeholder" onClick={() => setLightboxSrc('/MainMenuPOS.jpg')}>
                  <img src="/MainMenuPOS.jpg" alt="BigSystemOne Android POS order entry screen with categories, items, and open check" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Point of Sale</div>
                  <h2>A POS That Keeps Serving When the Internet Does Not</h2>
                  <p>
                    The BigSystemOne POS runs on Android tablets and terminals, stores every order locally, and syncs
                    the moment connectivity returns. Servers sign in with a PIN, open checks by table, send to the kitchen,
                    and take payment without leaving the screen.
                  </p>
                  <ul className="feature-list">
                    <li>Offline-first order entry with automatic background sync</li>
                    <li>Guest checks by table, hold and fire, item customization, send to kitchen</li>
                    <li>Cash, credit, and gift card payments through the Priv8 gateway</li>
                    <li>Host and waitlist mode, table selection, kitchen display, and printing on the same device</li>
                    <li>Multi-tenant by store code: register a device in seconds, reset it in ten</li>
                  </ul>
                  <div className="feature-tags">
                    <span className="feature-tag">Android</span>
                    <span className="feature-tag">Offline-first</span>
                    <span className="feature-tag">Square and Clover sync</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Menus and Pricing */}
          <section id="menus" className="feature-section feature-section-alt">
            <div className="container">
              <div className="feature-grid reversed">
                <div className="feature-image-placeholder" onClick={() => setLightboxSrc('/MenuManagement.png')}>
                  <img src="/MenuManagement.png" alt="Menu item management with photos, categories, and store assignment" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Menus and Pricing</div>
                  <h2>One Menu, Every Surface</h2>
                  <p>
                    Build the menu once and publish it to every store, POS, digital menu board, QR code, and online
                    ordering page. Price it differently by enterprise, concept, franchisee, or location without
                    maintaining copies.
                  </p>
                  <ul className="feature-list">
                    <li>Menu items, categories, and per-store assignment with CSV bulk import and PDF extraction</li>
                    <li>Universal modifier groups with industry templates</li>
                    <li>Hierarchical pricing, dayparts, tax rules, discounts, and promo codes</li>
                    <li>Digital menu boards with six display templates</li>
                    <li>QR code menus with online ordering, pickup or dine-in, and order tracking</li>
                    <li>AI-generated menu photography when you do not have a shot</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Front of House */}
          <section id="front-of-house" className="feature-section">
            <div className="container">
              <div className="feature-grid">
                <div className="feature-image-placeholder" onClick={() => setLightboxSrc('/screenshots/Screenshot-2026-03-09-at-12.00.03-PM.png')}>
                  <img src="/screenshots/Screenshot-2026-03-09-at-12.00.03-PM.png" alt="Table chart floor plan editor with sections, tables, and fixtures" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Front of House</div>
                  <h2>From the Door to the Table to the Pass</h2>
                  <p>
                    A digital waitlist that texts guests when their table is ready, a floor-plan editor that the POS
                    uses for seating, a kitchen display for the line, and a messaging system that gets alerts to the
                    right screen.
                  </p>
                  <ul className="feature-list">
                    <li>Waitlist with quoted wait times and SMS notifications</li>
                    <li>Floor-plan editor with sections, tables, walls, and fixtures, synced to the POS</li>
                    <li>Kitchen display system on the web and on Android</li>
                    <li>Typed, prioritized staff alerts: order ready, table seated, item 86'd, approval needed, broadcast</li>
                    <li>Manager approval workflow for voids, comps, and changes that need a second set of eyes</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Inventory and Kitchen Intelligence */}
          <section id="inventory" className="feature-section feature-section-alt">
            <div className="container">
              <div className="feature-grid reversed">
                <div className="feature-image-placeholder feature-image-panel">
                  <div className="feature-panel">
                    <div className="feature-panel-title">Inventory modules</div>
                    <ul className="feature-panel-list">
                      <li>Current Stock</li><li>Receiving</li><li>Recipes</li><li>Items Catalog</li>
                      <li>Suppliers</li><li>Forecast</li><li>Purchase Orders</li><li>Invoices</li>
                      <li>Waste</li><li>Network Alerts</li><li>Vision Zones</li><li>Reports</li>
                    </ul>
                  </div>
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Inventory and Kitchen Intelligence</div>
                  <h2>Know What You Have, What You Need, and What You Wasted</h2>
                  <p>
                    Recipes tie sales to ingredients, forecasts tie ingredients to orders, and receiving ties orders to
                    invoices. The result is a purchase order you can approve instead of a count you have to reconstruct.
                  </p>
                  <ul className="feature-list">
                    <li>Recipe bills of materials with nested sub-recipes</li>
                    <li>AI demand forecasting from sales history and recipes</li>
                    <li>AI-drafted purchase orders, receiving dock, and invoice scanning with matching</li>
                    <li>Supplier scorecards with lead times and fill rates</li>
                    <li>Waste analytics by cause, and network alerts for transfers between locations</li>
                    <li>Camera-based count zones on edge hardware, available for pilot deployments</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Guest Reputation and Marketing */}
          <section id="reputation" className="feature-section">
            <div className="container">
              <div className="feature-grid">
                <div className="feature-image-placeholder" onClick={() => setLightboxSrc('/screenshots/Screenshot-2026-03-09-at-11.49.35-AM.png')}>
                  <img src="/screenshots/Screenshot-2026-03-09-at-11.49.35-AM.png" alt="Review management dashboard with ratings and sentiment" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Guest Reputation and Marketing</div>
                  <h2>Answer Every Review, Recover Every Guest</h2>
                  <p>
                    Reviews from Google and Yelp land in one inbox with an AI-drafted response ready for your approval.
                    Unhappy guests move into a recovery workflow with follow-ups, escalation, and analytics, so a bad night
                    does not become a lost regular.
                  </p>
                  <ul className="feature-list">
                    <li>Review inbox, weekly scorecard, and AI-drafted replies for Google and Yelp</li>
                    <li>Guest recovery: response editor, follow-up log, escalation, configuration, and analytics</li>
                    <li>Social posting and scheduling for Facebook and Google Business Profile, with a shared calendar</li>
                    <li>Discount-code bank and offer distribution tied to campaigns</li>
                    <li>Micro-surveys guests can answer from a link or QR code</li>
                    <li>Review Autopilot onboarding that connects POS, loyalty, social, and review accounts in one pass</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Voice and Phone AI */}
          <section id="voice" className="feature-section feature-section-alt">
            <div className="container">
              <div className="feature-grid reversed">
                <div className="feature-image-placeholder" onClick={() => setLightboxSrc('/VoiceOrderingSim.png')}>
                  <img src="/VoiceOrderingSim.png" alt="Voice ordering simulator showing a conversation and the resulting order" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Voice and Phone AI</div>
                  <h2>Every Call Answered. Every Order Heard.</h2>
                  <p>
                    A realtime voice assistant answers your phone with your hours, menu, and policies, and logs every
                    call with its intent. At the counter, servers can speak an order and watch it land on the check.
                  </p>
                  <ul className="feature-list">
                    <li>AI phone assistant on your existing number, with call breakdown, intent distribution, and cost estimates</li>
                    <li>Voice ordering on the POS with combo suggestions and upsell rules you define</li>
                    <li>Simulator for testing flows before they go live</li>
                    <li>Session log, transcripts, guest feedback, context inspector, analytics, and reports</li>
                    <li>Pre-recorded phrases and per-store configuration</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* People and Scheduling */}
          <section id="people" className="feature-section">
            <div className="container">
              <div className="feature-grid">
                <div className="feature-image-placeholder feature-image-portrait" onClick={() => setLightboxSrc('/ScheduleApp.png')}>
                  <img src="/ScheduleApp.png" alt="Employee scheduling calendar" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">People and Scheduling</div>
                  <h2>Schedules Staff Can See, Rules Managers Can Trust</h2>
                  <p>
                    Employees, jobs, and labor rules live with the rest of the operation, so schedules respect
                    availability, overtime, and labor law, and the team sees changes on their phones.
                  </p>
                  <ul className="feature-list">
                    <li>Employee records, roles, and permissions backed by a single sign-on directory</li>
                    <li>Scheduling with templates, jobs, and labor rules</li>
                    <li>Alerts for conflicts, double bookings, overtime, labor-law violations, and shift pickups, by app, email, SMS, or push</li>
                    <li>Time-off and availability requests with approvals</li>
                    <li>Performance dashboard by employee, team, and store</li>
                    <li>Clock-in on the POS and a native employee app for schedules and requests</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Analytics and Command Center */}
          <section id="analytics" className="feature-section feature-section-alt">
            <div className="container">
              <div className="feature-grid reversed">
                <div className="feature-image-placeholder" onClick={() => setLightboxSrc('/WeatherDataManagement.png')}>
                  <img src="/WeatherDataManagement.png" alt="Weather analytics dashboard with forecast and daypart trends" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Analytics and Command Center</div>
                  <h2>Ask the Business a Question and Get an Answer</h2>
                  <p>
                    The command center puts revenue, menu performance, reviews and recovery, social, and team
                    leaderboards on one screen. Ask BSO lets you type a question in plain English and get the number.
                  </p>
                  <ul className="feature-list">
                    <li>Command center with revenue timeline, menu performance, recovery funnel, and server leaderboard</li>
                    <li>Ask BSO: natural-language questions answered from your own data</li>
                    <li>AI recommendations with explicit actions, such as pilot a price change or place an order now</li>
                    <li>Weather analytics per store, feeding daypart forecasting and scheduling</li>
                    <li>Store performance dashboards and an AI assistant available on every screen</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Enterprise Platform and API */}
          <section id="platform-api" className="feature-section">
            <div className="container">
              <div className="feature-grid">
                <div className="feature-image-placeholder" onClick={() => setLightboxSrc('/MultiUnitManagement.png')}>
                  <img src="/MultiUnitManagement.png" alt="Multi-unit management showing enterprise, concept, and store hierarchy" loading="lazy" />
                </div>
                <div className="feature-text">
                  <div className="feature-kicker">Enterprise Platform and API</div>
                  <h2>Built for Many Locations, Open to Your Systems</h2>
                  <p>
                    An enterprise hierarchy of concepts, franchisees, organizational units, and stores, with the
                    monitoring, security, and integration surface a multi-unit operator needs.
                  </p>
                  <ul className="feature-list">
                    <li>Enterprise, concept, franchisee, org unit, and store levels with permission templates and bulk operations</li>
                    <li>POS integrations with Square and Clover, plus an onboarding flow for loyalty and ordering providers</li>
                    <li>Equipment monitoring with sensor telemetry, and network management for UniFi sites and cameras</li>
                    <li>Security audit log, automated compliance checks, and generated compliance documentation</li>
                    <li>Support desk with live API health across every service</li>
                    <li>Public REST API with sandbox and production keys, scoped permissions, rate limits, and signed webhooks</li>
                  </ul>
                  <div className="feature-tags">
                    <Link to="/developers" className="feature-tag feature-tag-link">Developer portal &rarr;</Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Apps */}
        <section id="apps" className="apps-section">
          <div className="container">
            <div className="section-header">
              <h2>Native Apps for the Counter and the Team</h2>
              <p>The web platform runs the business. Two native apps put it in your staff's hands.</p>
            </div>
            <div className="apps-grid">
              <div className="app-card">
                <div className="app-card-image" onClick={() => setLightboxSrc('/KitchenDisplay.jpg')}>
                  <img src="/KitchenDisplay.jpg" alt="Kitchen display on the Android POS app" loading="lazy" />
                </div>
                <div className="app-card-body">
                  <div className="feature-kicker">Android</div>
                  <h3>BigSystemOne POS</h3>
                  <p>Order entry, guest checks, payments, kitchen display, host and waitlist mode, voice ordering, clock-in, and camera-based inventory counts. Offline-first, registered to a store in seconds.</p>
                </div>
              </div>
              <div className="app-card">
                <div className="app-card-image app-card-image-portrait" onClick={() => setLightboxSrc('/EmployeeApp.png')}>
                  <img src="/EmployeeApp.png" alt="BigSystemOne employee app on iPhone" loading="lazy" />
                </div>
                <div className="app-card-body">
                  <div className="feature-kicker">iOS</div>
                  <h3>BigSystemOne Employee</h3>
                  <p>Schedules, time-off and shift requests, profile and identity verification, plus manager views for guest recovery, social publishing, and voice reporting. Home-screen widgets and Live Activities surface new reviews, recovery cases, and waitlist pulse without opening the app.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* See It In Action - Screenshot Carousel */}
        <section className="screenshot-section">
          <div className="container">
            <div className="section-header">
              <h2>See It In Action</h2>
              <p>Real screens from the BigSystemOne platform and apps.</p>
            </div>
            <div className="screenshot-carousel">
              {screenshots.map((item) => (
                <div className="screenshot-card" key={item.src} onClick={() => setLightboxSrc(item.src)}>
                  <img src={item.src} alt={item.label} loading="lazy" />
                  <span className="screenshot-label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="how-it-works">
          <div className="container">
            <div className="section-header">
              <h2>How It Works</h2>
              <p>Start with the modules you need. Add the rest when you are ready; they already share your data.</p>
            </div>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Set Up Your Locations</h3>
                <p>Create your enterprise, concepts, and stores, invite your team, and import your menu by CSV, PDF, or by hand.</p>
              </div>
              <div className="step-connector"></div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Connect What You Already Use</h3>
                <p>Link your POS or register BigSystemOne devices, connect Google, Yelp, and Facebook, and point your phone number at the assistant.</p>
              </div>
              <div className="step-connector"></div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>Run the Restaurant</h3>
                <p>Take orders, seat guests, answer reviews, forecast inventory, schedule the team, and ask the command center how it is going.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="pricing-preview">
          <div className="container">
            <div className="section-header">
              <h2>Pricing</h2>
              <p>Plans for single locations through multi-unit enterprises.</p>
            </div>
            <div className="coming-soon-card">
              <div className="badge">Coming Soon</div>
              <h3>Published pricing is on its way</h3>
              <p>
                We are finalizing plans by location count and module. In the meantime, request a demo and we will
                put together pricing for your locations and the modules you want to start with.
              </p>
              <div className="cta-actions">
                <button className="btn-primary" onClick={() => setShowDemoModal(true)}>Request a Demo</button>
                <button className="btn-secondary" onClick={() => setShowGetStartedModal(true)}>Contact Sales</button>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Preview Section */}
        <section className="blog-preview-section">
          <div className="container">
            <div className="section-header">
              <h2>Latest Insights</h2>
              <p>Strategies for restaurant operators who want to grow smarter.</p>
            </div>
            <div className="blog-preview-grid">
              {(() => {
                const multiUnit = BLOG_POSTS.find(p => p.series === 'Multi-Unit Operations');
                const retention = BLOG_POSTS.find(p => p.series === 'Guest Retention' && p.category === 'The Power of Reviews');
                const tech = BLOG_POSTS.find(p => p.series === 'Guest Retention' && p.category === 'Technology & Human Touch');
                return [multiUnit, retention, tech].filter(Boolean).map(post => {
                  if (!post) return null;
                  const postSlug = post.slug || slugify(post.title);
                  return (
                    <Link to={`/blog/${postSlug}`} className="blog-card" key={post.id}>
                      <div className="blog-card-badges">
                        <span className="blog-badge blog-badge-series">{post.series}</span>
                        <span className="blog-badge blog-badge-category">{post.category}</span>
                      </div>
                      <h3>{post.title}</h3>
                      <p className="blog-card-excerpt">
                        {post.hook.length > 150 ? post.hook.slice(0, 150) + '...' : post.hook}
                      </p>
                      <span className="blog-card-link">Read More &rarr;</span>
                    </Link>
                  );
                });
              })()}
            </div>
            <div className="blog-preview-cta">
              <Link to="/blog">View All Posts</Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="faq-section">
          <div className="container">
            <div className="section-header">
              <h2>Frequently Asked Questions</h2>
            </div>
            <div className="faq-list">
              {faqItems.map((item, index) => (
                <div key={index} className={`faq-item ${openFaqIndex === index ? 'open' : ''}`}>
                  <button
                    className="faq-question"
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    aria-expanded={openFaqIndex === index}
                  >
                    <span>{item.question}</span>
                    <span className="faq-toggle">{openFaqIndex === index ? '-' : '+'}</span>
                  </button>
                  {openFaqIndex === index && (
                    <div className="faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="cta">
          <div className="container">
            <div className="cta-content">
              <h2>See the Whole Platform on Your Own Menu</h2>
              <p>Bring your locations, your current POS, and the problems you want solved first. We will show you how BigSystemOne handles them.</p>
              <div className="cta-actions">
                <button className="btn-primary-large" onClick={() => setShowDemoModal(true)}>Request a Demo</button>
                <button className="btn-secondary-large" onClick={() => setShowGetStartedModal(true)}>Contact Sales</button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <img src={logoBlack} alt="BigSystemOne" className="footer-logo" />
                <p>The Restaurant Operating System</p>
              </div>
              <div className="footer-links">
                <Link to="/developers">Developers</Link>
                <Link to="/blog">Blog</Link>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms of Service</Link>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 BigSystemOne. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {/* Get Started Modal */}
        <div className={`modal-overlay ${showGetStartedModal ? 'active' : ''}`} style={showGetStartedModal ? undefined : { visibility: 'hidden' }} onClick={(e) => {
          if (e.target === e.currentTarget) setShowGetStartedModal(false);
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>Contact Sales</h2>
              <p>Tell us about your locations and we will put together a plan and pricing for you.</p>
              <button
                className="modal-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGetStartedModal(false);
                }}
                type="button"
                aria-label="Close modal"
              >
                x
              </button>
            </div>
            <div className="modal-body">
              {submitSuccess ? (
                <div className="success-message">
                  <span className="success-icon">[OK]</span>
                  Thank you! We'll be in touch within 24 hours.
                </div>
              ) : null}
              {submitError && (
                <div className="error-message">
                  <span className="error-icon">!</span>
                  {submitError}
                </div>
              )}
              <form onSubmit={(e) => handleSubmit(e, 'getStarted')}>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className={`form-input ${formErrors.name ? 'error' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                  {formErrors.name && <div className="form-error show">{formErrors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Business Email</label>
                  <input
                    type="email"
                    className={`form-input ${formErrors.email ? 'error' : ''}`}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                  {formErrors.email && <div className="form-error show">{formErrors.email}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Restaurant Name</label>
                  <input
                    type="text"
                    className={`form-input ${formErrors.company ? 'error' : ''}`}
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    required
                  />
                  {formErrors.company && <div className="form-error show">{formErrors.company}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Phone <span>(optional)</span></label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tell us about your restaurant</label>
                  <textarea
                    className={`form-textarea ${formErrors.message ? 'error' : ''}`}
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Number of locations, current POS, which modules you want to start with..."
                    required
                  />
                  {formErrors.message && <div className="form-error show">{formErrors.message}</div>}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowGetStartedModal(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Schedule Demo Modal */}
        <div className={`modal-overlay ${showDemoModal ? 'active' : ''}`} style={showDemoModal ? undefined : { visibility: 'hidden' }} onClick={(e) => {
          if (e.target === e.currentTarget) setShowDemoModal(false);
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>Schedule a Demo</h2>
              <p>A walkthrough of the platform on your own menu and locations.</p>
              <button
                className="modal-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDemoModal(false);
                }}
                type="button"
                aria-label="Close modal"
              >
                x
              </button>
            </div>
            <div className="modal-body">
              {submitSuccess ? (
                <div className="success-message">
                  <span className="success-icon">[OK]</span>
                  Demo request received! We'll contact you within 24 hours to schedule.
                </div>
              ) : null}
              {submitError && (
                <div className="error-message">
                  <span className="error-icon">!</span>
                  {submitError}
                </div>
              )}
              <form onSubmit={(e) => handleSubmit(e, 'demo')}>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className={`form-input ${formErrors.name ? 'error' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                  {formErrors.name && <div className="form-error show">{formErrors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Business Email</label>
                  <input
                    type="email"
                    className={`form-input ${formErrors.email ? 'error' : ''}`}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                  {formErrors.email && <div className="form-error show">{formErrors.email}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Restaurant Name</label>
                  <input
                    type="text"
                    className={`form-input ${formErrors.company ? 'error' : ''}`}
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    required
                  />
                  {formErrors.company && <div className="form-error show">{formErrors.company}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Phone <span>(optional)</span></label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">What are you most interested in?</label>
                  <textarea
                    className={`form-textarea ${formErrors.message ? 'error' : ''}`}
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="POS, inventory, phone assistant, reviews, scheduling, analytics..."
                    required
                  />
                  {formErrors.message && <div className="form-error show">{formErrors.message}</div>}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDemoModal(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Request Demo'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* Screenshot Lightbox */}
        {lightboxSrc && (
          <div className="lightbox-overlay" onClick={() => setLightboxSrc(null)}>
            <button className="lightbox-close" onClick={() => setLightboxSrc(null)} aria-label="Close">x</button>
            <img src={lightboxSrc} alt="Screenshot enlarged view" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>
    </>
  );
};

export default EnhancedMarketingLandingPage;
